import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaUsers, FaBuilding, FaMoneyBillWave, FaUserCircle, FaSignOutAlt, FaPlus, FaBell, FaFileSignature,
  FaCheck, FaTimes, FaClock, FaChevronUp, FaEdit, FaTrash, FaDollarSign, FaPause, FaPlay, FaExchangeAlt, FaSitemap
} from 'react-icons/fa';
import {
  getClientes,
  createCliente,
  updateCliente,
  deleteCliente,
  getStats,
  adminLogout,
  getCompaniesByCliente,
  deactivateCompany,
  reactivateCompany,
  deleteCompany,
  bulkDeactivateCompanies,
  bulkDeleteCompanies
} from '../services/adminApi';
import { showError, showSuccess, showWarning } from '../utils/alerts';
import '../styles/pages/ManageClientsPage.css';

// Sub-componente para tabla de empresas
const CompaniesTable = ({
  companies,
  clienteId,
  selectedCompanies,
  onToggleSelection,
  onDeactivateCompany,
  onReactivateCompany,
  onDeleteCompany,
  onBulkDeactivate,
  onBulkDelete
}) => {
  if (companies.length === 0) {
    return <div className="no-companies">Este cliente no tiene empresas registradas.</div>;
  }

  const hasSelections = selectedCompanies.length > 0;

  return (
    <div className="companies-table-wrapper">
      <div className="companies-header">
        <h3>Empresas del Cliente ({companies.length})</h3>
        {hasSelections && (
          <div className="bulk-actions">
            <button
              onClick={() => onBulkDeactivate(clienteId)}
              className="btn-small btn-bulk-deactivate"
              title="Inactivar seleccionadas (reversible)"
            >
              <FaPause /> Inactivar ({selectedCompanies.length})
            </button>
            <button
              onClick={() => onBulkDelete(clienteId)}
              className="btn-small btn-bulk-delete"
              title="Eliminar permanentemente (NO reversible)"
            >
              <FaTrash /> Eliminar ({selectedCompanies.length})
            </button>
          </div>
        )}
      </div>

      <table className="companies-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={selectedCompanies.length === companies.length}
                onChange={(e) => {
                  companies.forEach(company => {
                    if (e.target.checked && !selectedCompanies.includes(company.id_company)) {
                      onToggleSelection(clienteId, company.id_company);
                    } else if (!e.target.checked && selectedCompanies.includes(company.id_company)) {
                      onToggleSelection(clienteId, company.id_company);
                    }
                  });
                }}
              />
            </th>
            <th>Nombre</th>
            <th>RUT</th>
            <th>Email</th>
            <th>Teléfono</th>
            <th>Estado</th>
            <th>Fecha Creación</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {companies.map((company) => (
            <tr key={company.id_company} className={!company.is_active ? 'inactive-company' : ''}>
              <td>
                <input
                  type="checkbox"
                  checked={selectedCompanies.includes(company.id_company)}
                  onChange={() => onToggleSelection(clienteId, company.id_company)}
                  disabled={!company.is_active}
                />
              </td>
              <td><strong>{company.name}</strong></td>
              <td>{company.rut || '-'}</td>
              <td>{company.email || '-'}</td>
              <td>{company.phone || '-'}</td>
              <td>
                <span className={`badge ${company.is_active ? 'badge-success' : 'badge-danger'}`}>
                  {company.is_active ? <><FaCheck /> Activa</> : <><FaTimes /> Inactiva</>}
                </span>
              </td>
              <td>
                {company.start_date
                  ? new Date(company.start_date).toLocaleDateString('es-CL')
                  : '-'}
              </td>
              <td>
                {company.is_active ? (
                  <div className="action-buttons">
                    <button
                      onClick={() => onDeactivateCompany(clienteId, company.id_company, company.name)}
                      className="btn-small btn-deactivate"
                      title="Inactivar (reversible)"
                    >
                      <FaPause />
                    </button>
                    <button
                      onClick={() => onDeleteCompany(clienteId, company.id_company, company.name)}
                      className="btn-small btn-delete"
                      title="Eliminar permanentemente"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => onReactivateCompany(clienteId, company.id_company, company.name)}
                    className="btn-small btn-reactivate"
                    title="Reactivar empresa"
                  >
                    <FaPlay /> Reactivar
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ManageClientsPage = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [expandedCliente, setExpandedCliente] = useState(null);
  const [companiesData, setCompaniesData] = useState({});
  const [loadingCompanies, setLoadingCompanies] = useState({});
  const [selectedCompanies, setSelectedCompanies] = useState({});

  const [formData, setFormData] = useState({
    nombre: '',
    correo: '',
    contraseña: '',
    maximo_empresas: 1,
    whatsapp: '',
    rut: ''
  });

  useEffect(() => {
    // Verificar que hay admin autenticado
    const storedAdmin = sessionStorage.getItem('adminData');
    if (!storedAdmin) {
      navigate('/admin-login');
      return;
    }
    setAdminData(JSON.parse(storedAdmin));

    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [clientesData, statsData] = await Promise.all([
        getClientes(),
        getStats()
      ]);
      setClientes(clientesData);
      setStats(statsData);
    } catch (error) {
      console.error('Error cargando datos:', error);
      showError('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingCliente) {
        await updateCliente(editingCliente.id, formData);
        showSuccess('Cliente actualizado exitosamente');
      } else {
        await createCliente(formData);
        showSuccess('Cliente creado exitosamente');
      }

      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error guardando cliente:', error);
      showError(error.response?.data?.message || 'Error guardando cliente');
    }
  };

  const handleEdit = (cliente) => {
    setFormData({
      nombre: cliente.nombre,
      correo: cliente.correo,
      contraseña: '', // No mostrar contraseña existente
      maximo_empresas: cliente.maximo_empresas,
      whatsapp: cliente.whatsapp || '',
      rut: cliente.rut || ''
    });
    setEditingCliente(cliente);
    setShowForm(true);
  };

  const handleDelete = async (id, nombre) => {
    if (!window.confirm(`¿Estás seguro de eliminar a ${nombre}?`)) {
      return;
    }

    try {
      const resp = await deleteCliente(id);
      if (resp.advertencia) showWarning(resp.message);
      else showSuccess(resp.message);
      fetchData();
    } catch (error) {
      console.error('Error eliminando cliente:', error);
      showError(error.response?.data?.message || 'Error eliminando cliente');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      correo: '',
      contraseña: '',
      maximo_empresas: 1,
      whatsapp: '',
      rut: ''
    });
    setEditingCliente(null);
    setShowForm(false);
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
      navigate('/admin-login');
    } catch (error) {
      console.error('Error cerrando sesión:', error);
    }
  };

  // Cargar empresas de un cliente
  const fetchCompanies = async (clienteId) => {
    if (companiesData[clienteId]) {
      setExpandedCliente(expandedCliente === clienteId ? null : clienteId);
      return;
    }

    try {
      setLoadingCompanies(prev => ({ ...prev, [clienteId]: true }));
      const companies = await getCompaniesByCliente(clienteId);
      setCompaniesData(prev => ({ ...prev, [clienteId]: companies }));
      setExpandedCliente(clienteId);
    } catch (error) {
      console.error('Error cargando empresas:', error);
      showError('Error cargando empresas del cliente');
    } finally {
      setLoadingCompanies(prev => ({ ...prev, [clienteId]: false }));
    }
  };

  // Toggle selección de empresa
  const toggleCompanySelection = (clienteId, companyId) => {
    setSelectedCompanies(prev => {
      const clientSelected = prev[clienteId] || [];
      const isSelected = clientSelected.includes(companyId);

      return {
        ...prev,
        [clienteId]: isSelected
          ? clientSelected.filter(id => id !== companyId)
          : [...clientSelected, companyId]
      };
    });
  };

  // Inactivar una empresa (soft delete)
  const handleDeactivateCompany = async (clienteId, companyId, companyName) => {
    if (!window.confirm(`¿Estás seguro de inactivar la empresa "${companyName}"?\n\nEsta acción se puede revertir.`)) {
      return;
    }

    try {
      await deactivateCompany(companyId);
      showSuccess('Empresa inactivada exitosamente');

      setCompaniesData(prev => ({ ...prev, [clienteId]: undefined }));
      await fetchCompanies(clienteId);
      fetchData();
    } catch (error) {
      console.error('Error inactivando empresa:', error);
      showError('Error inactivando empresa');
    }
  };

  // Reactivar una empresa
  const handleReactivateCompany = async (clienteId, companyId, companyName) => {
    if (!window.confirm(`¿Estás seguro de reactivar la empresa "${companyName}"?`)) {
      return;
    }

    try {
      await reactivateCompany(companyId);
      showSuccess('Empresa reactivada exitosamente');

      setCompaniesData(prev => ({ ...prev, [clienteId]: undefined }));
      await fetchCompanies(clienteId);
      fetchData();
    } catch (error) {
      console.error('Error reactivando empresa:', error);
      showError('Error reactivando empresa');
    }
  };

  // Eliminar una empresa permanentemente (hard delete)
  const handleDeleteCompany = async (clienteId, companyId, companyName) => {
    if (!window.confirm(`⚠️ ¿Estás seguro de ELIMINAR PERMANENTEMENTE la empresa "${companyName}"?\n\n⚠️ ESTA ACCIÓN NO SE PUEDE REVERTIR.\n\nSe eliminará la empresa y todas sus relaciones.`)) {
      return;
    }

    try {
      const resp = await deleteCompany(companyId);
      if (resp.advertencia) showWarning(resp.message);
      else showSuccess(resp.message);

      setCompaniesData(prev => ({ ...prev, [clienteId]: undefined }));
      await fetchCompanies(clienteId);
      fetchData();
    } catch (error) {
      console.error('Error eliminando empresa:', error);
      showError(error.response?.data?.message || 'Error eliminando empresa');
    }
  };

  // Inactivación masiva (soft delete)
  const handleBulkDeactivateCompanies = async (clienteId) => {
    const selected = selectedCompanies[clienteId] || [];

    if (selected.length === 0) {
      showError('Selecciona al menos una empresa para inactivar');
      return;
    }

    if (!window.confirm(`¿Estás seguro de inactivar ${selected.length} empresa(s)?\n\nEsta acción se puede revertir.`)) {
      return;
    }

    try {
      await bulkDeactivateCompanies(selected);
      showSuccess(`${selected.length} empresa(s) inactivada(s) exitosamente`);

      setSelectedCompanies(prev => ({ ...prev, [clienteId]: [] }));
      setCompaniesData(prev => ({ ...prev, [clienteId]: undefined }));
      await fetchCompanies(clienteId);
      fetchData();
    } catch (error) {
      console.error('Error inactivando empresas:', error);
      showError('Error inactivando empresas');
    }
  };

  // Eliminación masiva permanente (hard delete)
  const handleBulkDeleteCompanies = async (clienteId) => {
    const selected = selectedCompanies[clienteId] || [];

    if (selected.length === 0) {
      showError('Selecciona al menos una empresa para eliminar');
      return;
    }

    if (!window.confirm(`⚠️ ¿Estás seguro de ELIMINAR PERMANENTEMENTE ${selected.length} empresa(s)?\n\n⚠️ ESTA ACCIÓN NO SE PUEDE REVERTIR.`)) {
      return;
    }

    try {
      // Responde 200 aunque falle alguna empresa: el resumen dice cuál, y el refresco corre igual
      const resp = await bulkDeleteCompanies(selected);
      if (resp.advertencia) showWarning(resp.message);
      else showSuccess(resp.message);

      setSelectedCompanies(prev => ({ ...prev, [clienteId]: [] }));
      setCompaniesData(prev => ({ ...prev, [clienteId]: undefined }));
      await fetchCompanies(clienteId);
      fetchData();
    } catch (error) {
      console.error('Error eliminando empresas:', error);
      showError(error.response?.data?.message || 'Error eliminando empresas');
    }
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="manage-clients-page">
      {/* Header */}
      <header className="admin-header">
        <div className="header-content">
          <h1>Panel de Administración</h1>
          <div className="admin-info">
            <span><FaUserCircle /> {adminData?.nombre}</span>
            <button onClick={handleLogout} className="btn-logout">
              <FaSignOutAlt /> Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      {/* Estadísticas */}
      {stats && (
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-icon"><FaUsers /></div>
            <div className="stat-content">
              <h3>Total Clientes</h3>
              <p className="stat-number">{stats.total_clientes}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><FaBuilding /></div>
            <div className="stat-content">
              <h3>Total Empresas</h3>
              <p className="stat-number">{stats.total_empresas}</p>
            </div>
          </div>
          <div className="stat-card info">
            <div className="stat-icon"><FaMoneyBillWave /></div>
            <div className="stat-content">
              <h3>Total Recaudado</h3>
              <p className="stat-number">${Math.round(Number(stats.total_recaudado) || 0).toLocaleString('es-CL')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Botón crear cliente */}
      <div className="actions-bar">
        <button
          className="btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancelar' : <><FaPlus /> Crear Nuevo Cliente</>}
        </button>
        <button
          className="btn-secondary"
          onClick={() => navigate('/admin/notificaciones')}
        >
          <FaBell /> Notificaciones
        </button>
        <button
          className="btn-secondary"
          onClick={() => navigate('/admin/facturador')}
        >
          <FaFileSignature /> Facturador
        </button>
        <button
          className="btn-secondary"
          onClick={() => navigate('/admin/inversiones-exterior/tipos-cambio')}
        >
          <FaExchangeAlt /> Tipos de cambio
        </button>
        <button
          className="btn-secondary"
          onClick={() => navigate('/admin/plan-cuentas')}
        >
          <FaSitemap /> Plan de cuentas
        </button>
      </div>

      {/* Formulario */}
      {showForm && (
        <div className="client-form-container">
          <h2>{editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}</h2>
          <form onSubmit={handleSubmit} className="client-form">
            <div className="form-row">
              <div className="form-group">
                <label>Nombre completo *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  placeholder="Ej: Juan Pérez"
                  required
                />
              </div>
              <div className="form-group">
                <label>Correo electrónico *</label>
                <input
                  type="email"
                  name="correo"
                  value={formData.correo}
                  onChange={handleInputChange}
                  placeholder="cliente@example.com"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Contraseña {editingCliente ? '(dejar vacío para no cambiar)' : '*'}</label>
                <input
                  type="password"
                  name="contraseña"
                  value={formData.contraseña}
                  onChange={handleInputChange}
                  placeholder="Contraseña"
                  required={!editingCliente}
                />
              </div>
              <div className="form-group">
                <label>RUT {!editingCliente && '*'}</label>
                <input
                  type="text"
                  name="rut"
                  value={formData.rut}
                  onChange={handleInputChange}
                  placeholder="12.345.678-9"
                  required={!editingCliente}
                />
                <small>Formato: 12.345.678-9</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>WhatsApp</label>
                <input
                  type="tel"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleInputChange}
                  placeholder="+56912345678"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Máximo de empresas *</label>
                <input
                  type="number"
                  name="maximo_empresas"
                  value={formData.maximo_empresas}
                  onChange={handleInputChange}
                  min="1"
                  required
                />
                <small>Número máximo de empresas que puede crear el cliente</small>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-success">
                {editingCliente ? 'Actualizar Cliente' : 'Crear Cliente'}
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tabla de clientes */}
      <div className="clients-table-container">
        <h2>Clientes Registrados ({clientes.length})</h2>
        <div className="table-responsive">
          <table className="clients-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Correo</th>
                <th>WhatsApp</th>
                <th>Empresas</th>
                <th>Límite</th>
                <th>Pagos</th>
                <th>Ver Empresas</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {clientes.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-data">
                    No hay clientes registrados
                  </td>
                </tr>
              ) : (
                clientes.map((cliente) => (
                  <Fragment key={cliente.id}>
                    <tr>
                      <td>{cliente.id}</td>
                      <td><strong>{cliente.nombre}</strong></td>
                      <td>{cliente.correo}</td>
                      <td>{cliente.whatsapp || '-'}</td>
                      <td>{cliente.total_empresas || 0}</td>
                      <td>{cliente.maximo_empresas}</td>
                      <td>
                        <span className="badge badge-success" title="Pagos realizados">
                          <FaCheck /> {cliente.pagos_realizados || 0}
                        </span>
                        <span className="badge badge-warning" title="Pagos pendientes">
                          <FaClock /> {cliente.pagos_pendientes || 0}
                        </span>
                      </td>

                      {/* NUEVA COLUMNA - Botón Ver Empresas */}
                      <td>
                        <button
                          onClick={() => fetchCompanies(cliente.id)}
                          className="btn-small btn-companies"
                          title={expandedCliente === cliente.id ? 'Ocultar empresas' : 'Ver empresas'}
                        >
                          {loadingCompanies[cliente.id] ? <FaClock /> : expandedCliente === cliente.id ? <FaChevronUp /> : <FaBuilding />}
                        </button>
                      </td>

                      <td className="actions">
                        <button
                          onClick={() => handleEdit(cliente)}
                          className="btn-small btn-edit"
                          title="Editar"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(cliente.id, cliente.nombre)}
                          className="btn-small btn-delete"
                          title="Eliminar"
                        >
                          <FaTrash />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/clientes/${cliente.id}/pagos`)}
                          className="btn-small btn-info"
                          title="Ver pagos"
                        >
                          <FaDollarSign />
                        </button>
                      </td>
                    </tr>

                    {/* FILA EXPANDIBLE con tabla de empresas */}
                    {expandedCliente === cliente.id && (
                      <tr className="expanded-row">
                        <td colSpan="12" className="expanded-content">
                          {loadingCompanies[cliente.id] ? (
                            <div className="loading-companies">Cargando empresas...</div>
                          ) : (
                            <CompaniesTable
                              companies={companiesData[cliente.id] || []}
                              clienteId={cliente.id}
                              selectedCompanies={selectedCompanies[cliente.id] || []}
                              onToggleSelection={toggleCompanySelection}
                              onDeactivateCompany={handleDeactivateCompany}
                              onReactivateCompany={handleReactivateCompany}
                              onDeleteCompany={handleDeleteCompany}
                              onBulkDeactivate={handleBulkDeactivateCompanies}
                              onBulkDelete={handleBulkDeleteCompanies}
                            />
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManageClientsPage;
